import { useEffect, useId, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api, type TelegramConnection } from '../../api/client';
import { ApiKeyManagement } from '../ApiKeyManagement';
import { PublicApiKeys } from '../PublicApiKeys';
import { FeatherIcon } from '../FeatherIcon';
import { Btn, Card, Icon, Row, Section, Stack, TextInput } from '../ChatAssistant/compact/kit';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { AVATAR_ACCEPT, prepareAvatar } from '../../utils/avatarImage';
import { useTelegramConnect } from '../Telegram/useTelegramConnect';
import { BusyLabel } from '../Spinner';

/** A status line under a form: purple for both outcomes (the compact palette has no green/red). */
export function StatusLine({ kind, children }: { kind: 'error' | 'ok'; children: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: kind === 'error' ? C.purplePale : C.sub }}>
      <Icon name={kind === 'error' ? 'alert-circle' : 'check'} size={13} />
      {children}
    </span>
  );
}

/** The User's Telegram connection: who it is, and Reconnect. It can be replaced, never removed. */
function TelegramRow() {
  const { t } = useTranslation('settings');
  const [connection, setConnection] = useState<TelegramConnection | null>(null);
  const { state, start } = useTelegramConnect(setConnection);

  useEffect(() => {
    api.getTelegramConnection().then(setConnection).catch(() => setConnection(null));
  }, []);

  const help =
    state === 'waiting' ? t('telegramWaiting')
      : state === 'expired' ? <StatusLine kind="error">{t('telegramExpired')}</StatusLine>
        : state === 'error' ? <StatusLine kind="error">{t('telegramFailed')}</StatusLine>
          : connection?.unreachable ? <StatusLine kind="error">{t('telegramUnreachable')}</StatusLine>
            : undefined;

  return (
    <Row stack label={t('telegram')} help={help}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ font: f(500, 12.5), color: connection?.connected ? C.text : C.sub }}>
          {!connection?.connected ? t('telegramNotConnected')
            : connection.username ? `@${connection.username}` : t('telegramNoUsername')}
        </span>
        <Btn kind={connection?.connected ? 'muted' : 'primary'} onClick={() => void start()} disabled={state === 'opening' || state === 'waiting'}>
          {connection?.connected ? t('telegramReconnect') : t('telegramConnect')}
        </Btn>
      </div>
    </Row>
  );
}

const passwordInput: CSSProperties = {
  width: '100%', height: 32, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6,
  padding: '0 38px 0 10px', color: C.text, font: f(500, 13), outline: 'none',
};

function PasswordField({ id, label, hint, value, onChange, visible, onToggle, toggleLabel, autoComplete, describedBy }: {
  id: string; label: string; hint?: string; value: string; onChange: (v: string) => void;
  visible: boolean; onToggle?: () => void; toggleLabel?: string; autoComplete: string; describedBy: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <label htmlFor={id} style={{ font: f(500, 13), color: C.text }}>{label}</label>
        {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
      </div>
      <div style={{ position: 'relative', maxWidth: 420 }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          minLength={autoComplete === 'new-password' ? 8 : undefined}
          maxLength={128}
          required
          aria-describedby={describedBy}
          style={passwordInput}
        />
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={toggleLabel}
            aria-pressed={visible}
            className="cpt-hover-text"
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', color: C.muted, cursor: 'pointer' }}
          >
            <FeatherIcon name={visible ? 'eye-off' : 'eye'} size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

/** The profile picture row: preview + upload / remove. Saves on pick, no Save button. */
function PictureRow() {
  const { t } = useTranslation('settings');
  const { user, setAvatarUrl } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const url = user?.avatar_url || null;
  const handle = user?.username || user?.email || '';

  const pick = async (file: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!file) return;
    setStatus(null);
    let image: Blob;
    try {
      image = await prepareAvatar(file);
    } catch {
      setStatus({ kind: 'error', text: t('pictureInvalid') });
      return;
    }
    setBusy(true);
    try {
      const res = await api.uploadMyAvatar(image);
      setAvatarUrl(res.avatar_url ?? null);
      setStatus({ kind: 'ok', text: t('pictureSaved') });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error && err.message ? err.message : t('pictureFailed') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setStatus(null);
    setBusy(true);
    try {
      await api.deleteMyAvatar();
      setAvatarUrl(null);
      setStatus({ kind: 'ok', text: t('pictureRemoved') });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error && err.message ? err.message : t('pictureFailed') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row stack label={t('picture')} help={status ? <StatusLine kind={status.kind}>{status.text}</StatusLine> : t('pictureHint')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          aria-label={url ? t('pictureChange') : t('pictureUpload')}
          className="cpt-hover-border"
          style={{
            // A picture is drawn bare: a fill or border behind it would show through
            // transparent corners and rim the edge of round logos.
            width: 56, height: 56, flex: 'none', padding: 0, borderRadius: 99, overflow: 'hidden', border: url ? 0 : `1px solid ${C.border}`,
            background: url ? 'transparent' : C.purple, color: '#fff', boxShadow: url ? `0 0 0 1px ${C.border}` : undefined, font: f(600, 20), display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}
        >
          {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (handle.charAt(0).toUpperCase() || '?')}
        </button>
        <Btn kind="ghost" icon="upload" onClick={() => input.current?.click()} disabled={busy} busy={busy}>
          {url ? t('pictureChange') : t('pictureUpload')}
        </Btn>
        {url && <Btn kind="muted" onClick={() => void remove()} disabled={busy}>{t('pictureRemove')}</Btn>}
        <input ref={input} type="file" accept={AVATAR_ACCEPT} hidden onChange={(e) => void pick(e.target.files?.[0])} />
      </div>
    </Row>
  );
}

/** Settings → Account: the signed-in user's profile, password and API key. */
export function AccountTab() {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const [username, setUsername] = useState(user?.username || '');
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPasswords, setShowNewPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const passwordStatusId = useId();
  const idPrefix = useId();

  const handleSaveUsername = async () => {
    setUsernameStatus(null);
    const trimmed = username.trim();
    if (!trimmed) {
      setUsernameStatus({ kind: 'error', text: t('usernameEmpty') });
      return;
    }
    if (!/^[a-zA-Z0-9_-]{3,50}$/.test(trimmed)) {
      setUsernameStatus({ kind: 'error', text: t('usernameInvalid') });
      return;
    }
    setSavingUsername(true);
    try {
      await api.updateMyProfile({ username: trimmed });
      setUsernameStatus({ kind: 'ok', text: t('usernameSuccess') });
    } catch (err) {
      setUsernameStatus({ kind: 'error', text: err instanceof Error ? err.message : t('usernameFailed') });
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordStatus(null);
    const fail = (text: string) => setPasswordStatus({ kind: 'error', text });
    if (newPassword.length < 8) return fail(t('passwordTooShort'));
    if (newPassword !== confirmPassword) return fail(t('passwordMismatch'));
    if (currentPassword === newPassword) return fail(t('passwordUnchanged'));

    setSavingPassword(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStatus({ kind: 'ok', text: t('passwordSuccess') });
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message === 'Current password is incorrect') fail(t('currentPasswordIncorrect'));
      else if (message === 'New password must be different from current password') fail(t('passwordUnchanged'));
      else if (message === 'Auth service unavailable') fail(t('authUnavailable'));
      else fail(t('passwordFailed'));
    } finally {
      setSavingPassword(false);
    }
  };

  const editPassword = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPasswordStatus(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760, width: '100%' }}>
      <Stack gap={18}>
        <Section title={t('profile')}>
          <Card>
            <PictureRow />
            <Row label={t('email')}>
              <span style={{ font: f(500, 12.5), color: C.text, whiteSpace: 'normal', wordBreak: 'break-all' }}>{user?.email}</span>
            </Row>
            <Row stack label={t('username')} help={usernameStatus ? <StatusLine kind={usernameStatus.kind}>{usernameStatus.text}</StatusLine> : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420 }}>
                <TextInput
                  value={username}
                  onChange={(v) => {
                    setUsername(v);
                    setUsernameStatus(null);
                  }}
                  placeholder={t('usernamePlaceholder')}
                />
                <Btn kind="primary" onClick={() => void handleSaveUsername()} disabled={savingUsername}>
                  <BusyLabel busy={savingUsername} label={tCommon('saving')}>{tCommon('save')}</BusyLabel>
                </Btn>
              </div>
            </Row>
            <TelegramRow />
          </Card>
        </Section>

        <Section title={t('password')} hint={t('passwordHint')}>
          <Card>
            <form onSubmit={(e) => void handleChangePassword(e)}>
              <PasswordField
                id={`${idPrefix}-current`}
                label={t('currentPassword')}
                value={currentPassword}
                onChange={editPassword(setCurrentPassword)}
                visible={showCurrentPassword}
                onToggle={() => setShowCurrentPassword((v) => !v)}
                toggleLabel={showCurrentPassword ? t('hidePassword') : t('showPassword')}
                autoComplete="current-password"
                describedBy={passwordStatusId}
              />
              <PasswordField
                id={`${idPrefix}-new`}
                label={t('newPassword')}
                hint={t('passwordRequirements')}
                value={newPassword}
                onChange={editPassword(setNewPassword)}
                visible={showNewPasswords}
                onToggle={() => setShowNewPasswords((v) => !v)}
                toggleLabel={showNewPasswords ? t('hideNewPasswords') : t('showNewPasswords')}
                autoComplete="new-password"
                describedBy={passwordStatusId}
              />
              <PasswordField
                id={`${idPrefix}-confirm`}
                label={t('confirmPassword')}
                value={confirmPassword}
                onChange={editPassword(setConfirmPassword)}
                visible={showNewPasswords}
                autoComplete="new-password"
                describedBy={passwordStatusId}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '6px 14px 12px' }}>
                <button
                  type="submit"
                  disabled={savingPassword}
                  style={{ height: 28, padding: '0 11px', borderRadius: 6, border: 0, background: C.purple, color: '#fff', font: f(600, 12), cursor: savingPassword ? 'default' : 'pointer', opacity: savingPassword ? 0.5 : 1 }}
                >
                  <BusyLabel busy={savingPassword} label={tCommon('saving')}>{t('changePassword')}</BusyLabel>
                </button>
                <div id={passwordStatusId} aria-live="polite">
                  {passwordStatus && <StatusLine kind={passwordStatus.kind}>{passwordStatus.text}</StatusLine>}
                </div>
              </div>
            </form>
          </Card>
        </Section>

        <PublicApiKeys />

        <ApiKeyManagement />
      </Stack>
    </div>
  );
}
